import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Copy, Plus, Power, Save, Trash2 } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import {
  agentSkillsApi,
  type AgentSkill,
  type AgentSkillInput,
  type SkillScope,
} from '@/modules/agent-skills/services/agentSkills.api';
import { BrainScopeRail, type ScopeSelection, type ScopeWorkspace } from './BrainScopeRail';

const SCOPE_QUERY = 'scope';
const WS_QUERY = 'ws';
const MODE_QUERY = 'mode';
const EDIT_QUERY = 'edit';
const DUPLICATE_QUERY = 'duplicate';

const AGENT_OPTIONS = [
  { value: '', label: 'All agents' },
  { value: 'search_v3', label: 'Search v3' },
  { value: 'search_v2', label: 'Search v2' },
  { value: 'search', label: 'Search' },
  { value: 'job_create', label: 'Job create' },
  { value: 'job_edit', label: 'Job edit' },
];

type Draft = AgentSkillInput;

type EditorState =
  | { kind: 'create'; key: string; draft: Draft; existing: null }
  | { kind: 'edit'; key: string; draft: Draft; existing: AgentSkill }
  | { kind: 'duplicate'; key: string; draft: Draft; existing: null; source: AgentSkill };

function scopeOf(skill: AgentSkill): SkillScope {
  return skill.scope || (skill.userId ? 'personal' : skill.workspaceId ? 'workspace' : 'organization');
}

function scopeSelectionFromSkill(skill: AgentSkill): ScopeSelection {
  const skillScope = scopeOf(skill);
  if (skillScope === 'personal') return { kind: 'personal' };
  if (skillScope === 'workspace' && skill.workspaceId) return { kind: 'workspace', workspaceId: skill.workspaceId };
  return { kind: 'org' };
}

function readScope(params: URLSearchParams): ScopeSelection | null {
  const ws = params.get(WS_QUERY);
  if (ws) return { kind: 'workspace', workspaceId: ws };
  const scope = params.get(SCOPE_QUERY);
  if (scope === 'personal') return { kind: 'personal' };
  if (scope === 'org' || scope === 'organization') return { kind: 'org' };
  return null;
}

function writeScope(params: URLSearchParams, scope: ScopeSelection): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(SCOPE_QUERY);
  next.delete(WS_QUERY);
  if (scope.kind === 'personal') next.set(SCOPE_QUERY, 'personal');
  if (scope.kind === 'org') next.set(SCOPE_QUERY, 'org');
  if (scope.kind === 'workspace') next.set(WS_QUERY, scope.workspaceId);
  return next;
}

function clearEditorParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(MODE_QUERY);
  next.delete(EDIT_QUERY);
  next.delete(DUPLICATE_QUERY);
  return next;
}

function scopeToPayload(scope: ScopeSelection): Pick<AgentSkillInput, 'scope' | 'workspaceId'> {
  if (scope.kind === 'personal') return { scope: 'personal' };
  if (scope.kind === 'workspace') return { scope: 'workspace', workspaceId: scope.workspaceId };
  return { scope: 'organization' };
}

function slugify(value: string) {
  const slug = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
  return /^[a-z]/.test(slug) ? slug : `skill-${slug || 'new'}`.slice(0, 64);
}

function sanitizeSlugInput(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .slice(0, 64);
}

function emptyDraft(scope: ScopeSelection): Draft {
  return {
    ...scopeToPayload(scope),
    name: '',
    displayName: '',
    description: '',
    whenToUse: '',
    contentMarkdown: '',
    agentTypes: [],
    isActive: true,
    order: 0,
  };
}

function draftFromSkill(skill: AgentSkill): Draft {
  return {
    scope: scopeOf(skill),
    workspaceId: skill.workspaceId || undefined,
    name: skill.name,
    displayName: skill.displayName,
    description: skill.description,
    whenToUse: skill.whenToUse,
    contentMarkdown: skill.contentMarkdown,
    agentTypes: skill.agentTypes,
    isActive: skill.isActive,
    order: skill.order,
  };
}

function duplicateDraft(skill: AgentSkill, scope: ScopeSelection): Draft {
  return {
    ...emptyDraft(scope),
    name: `${skill.name}-copy`.slice(0, 64),
    displayName: `${skill.displayName} copy`.trim(),
    description: skill.description,
    whenToUse: skill.whenToUse,
    contentMarkdown: skill.contentMarkdown,
    agentTypes: skill.agentTypes,
    isActive: skill.isActive,
    order: skill.order,
  };
}

export function SkillsPage() {
  const { hasPermission, currentWorkspaceId, workspaces } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [skills, setSkills] = useState<AgentSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [agentFilter, setAgentFilter] = useState('');

  const canManageOrg = hasPermission('skill.org.manage');
  const canManageWorkspace = hasPermission('skill.workspace.manage');
  const canViewOrg = hasPermission('skill.org.view') || canManageOrg;
  const canViewWorkspace = hasPermission('skill.workspace.view') || canManageWorkspace;

  const accessibleWorkspaces: ScopeWorkspace[] = useMemo(() => {
    if (!canViewWorkspace) return [];
    return workspaces.map((ws) => ({
      _id: ws._id,
      name: ws.name,
      current: ws._id === currentWorkspaceId,
    }));
  }, [canViewWorkspace, workspaces, currentWorkspaceId]);

  const defaultScope: ScopeSelection = { kind: 'personal' };
  const scope = readScope(searchParams) ?? defaultScope;

  const goToList = useCallback(
    (nextScope: ScopeSelection = scope) => {
      setSearchParams(clearEditorParams(writeScope(searchParams, nextScope)));
    },
    [scope, searchParams, setSearchParams],
  );

  const setScope = (next: ScopeSelection) => goToList(next);

  const canManageScope = useCallback(
    (skillScope: SkillScope) =>
      skillScope === 'personal' ||
      (skillScope === 'organization' && canManageOrg) ||
      (skillScope === 'workspace' && canManageWorkspace),
    [canManageOrg, canManageWorkspace],
  );

  const canManageHere =
    scope.kind === 'personal' ||
    (scope.kind === 'org' && canManageOrg) ||
    (scope.kind === 'workspace' && canManageWorkspace);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await agentSkillsApi.list();
      setSkills(data);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const scopeLabel = (() => {
    if (scope.kind === 'personal') return 'Personal';
    if (scope.kind === 'org') return 'Organization';
    return accessibleWorkspaces.find((ws) => ws._id === scope.workspaceId)?.name || 'Workspace';
  })();

  const visibleSkills = useMemo(() => {
    const q = query.trim().toLowerCase();
    return skills.filter((skill) => {
      const skillScope = scopeOf(skill);
      if (scope.kind === 'personal' && skillScope !== 'personal') return false;
      if (scope.kind === 'org' && skillScope !== 'organization') return false;
      if (scope.kind === 'workspace' && skill.workspaceId !== scope.workspaceId) return false;
      if (statusFilter === 'active' && !skill.isActive) return false;
      if (statusFilter === 'inactive' && skill.isActive) return false;
      if (agentFilter && skill.agentTypes.length > 0 && !skill.agentTypes.includes(agentFilter)) return false;
      if (!q) return true;
      return [skill.name, skill.displayName, skill.description, skill.whenToUse, skill.contentMarkdown]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [agentFilter, query, scope, skills, statusFilter]);

  const editorState = useMemo<EditorState | null>(() => {
    const editId = searchParams.get(EDIT_QUERY);
    const duplicateId = searchParams.get(DUPLICATE_QUERY);
    const mode = searchParams.get(MODE_QUERY);

    if (editId) {
      const skill = skills.find((item) => item._id === editId);
      if (!skill) return null;
      return { kind: 'edit', key: `edit:${skill._id}:${skill.version}`, draft: draftFromSkill(skill), existing: skill };
    }

    if (duplicateId) {
      const skill = skills.find((item) => item._id === duplicateId);
      if (!skill) return null;
      return {
        kind: 'duplicate',
        key: `duplicate:${skill._id}:${scope.kind}:${scope.kind === 'workspace' ? scope.workspaceId : ''}`,
        draft: duplicateDraft(skill, scope),
        existing: null,
        source: skill,
      };
    }

    if (mode === 'create') {
      return {
        kind: 'create',
        key: `create:${scope.kind}:${scope.kind === 'workspace' ? scope.workspaceId : ''}`,
        draft: emptyDraft(scope),
        existing: null,
      };
    }

    return null;
  }, [scope, searchParams, skills]);

  const isEditingRoute =
    searchParams.get(MODE_QUERY) === 'create' ||
    Boolean(searchParams.get(EDIT_QUERY)) ||
    Boolean(searchParams.get(DUPLICATE_QUERY));

  const openCreate = () => {
    const next = clearEditorParams(new URLSearchParams(searchParams));
    next.set(MODE_QUERY, 'create');
    setSearchParams(next);
  };

  const openEdit = (skill: AgentSkill) => {
    const next = clearEditorParams(writeScope(searchParams, scopeSelectionFromSkill(skill)));
    next.set(EDIT_QUERY, skill._id);
    setSearchParams(next);
  };

  const openDuplicate = (skill: AgentSkill) => {
    const next = clearEditorParams(new URLSearchParams(searchParams));
    next.set(DUPLICATE_QUERY, skill._id);
    setSearchParams(next);
  };

  const saveDraft = async (state: EditorState, nextDraft: Draft) => {
    try {
      if (state.kind === 'edit') {
        const updated = await agentSkillsApi.update(state.existing._id, {
          displayName: nextDraft.displayName,
          description: nextDraft.description,
          whenToUse: nextDraft.whenToUse,
          contentMarkdown: nextDraft.contentMarkdown,
          agentTypes: nextDraft.agentTypes,
          isActive: nextDraft.isActive,
          order: nextDraft.order,
        });
        setSkills((prev) => prev.map((skill) => (skill._id === updated._id ? updated : skill)));
        goToList(scopeSelectionFromSkill(updated));
      } else {
        const created = await agentSkillsApi.create(nextDraft);
        setSkills((prev) => [...prev, created]);
        goToList(scopeSelectionFromSkill(created));
      }
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save skill');
    }
  };

  const toggleSkill = async (skill: AgentSkill) => {
    const optimistic = !skill.isActive;
    setSkills((prev) => prev.map((item) => (item._id === skill._id ? { ...item, isActive: optimistic } : item)));
    try {
      const updated = await agentSkillsApi.update(skill._id, { isActive: optimistic });
      setSkills((prev) => prev.map((item) => (item._id === updated._id ? updated : item)));
    } catch (err: unknown) {
      setSkills((prev) => prev.map((item) => (item._id === skill._id ? skill : item)));
      setError(err instanceof Error ? err.message : 'Failed to update skill');
    }
  };

  const deleteSkill = async (skill: AgentSkill) => {
    if (!confirm(`Delete "${skill.displayName}"?`)) return;
    const backup = skills;
    setSkills((prev) => prev.filter((item) => item._id !== skill._id));
    try {
      await agentSkillsApi.delete(skill._id);
    } catch (err: unknown) {
      setSkills(backup);
      setError(err instanceof Error ? err.message : 'Failed to delete skill');
    }
  };

  return (
    <div className="brain-page">
      <BrainScopeRail
        title="Skills"
        current={scope}
        showPersonal
        showOrg={canViewOrg}
        showWorkspaces
        workspaces={accessibleWorkspaces}
        onSelect={setScope}
      />

      {isEditingRoute ? (
        <section className="brain-content brain-skill-editor-content">
          {loading ? (
            <div className="brain-loading">Loading...</div>
          ) : editorState ? (
            <SkillEditorPage
              key={editorState.key}
              state={editorState}
              workspaces={accessibleWorkspaces}
              canManageOrg={canManageOrg}
              canManageWorkspace={canManageWorkspace}
              canSave={
                editorState.kind === 'edit'
                  ? canManageScope(scopeOf(editorState.existing))
                  : canManageScope(editorState.draft.scope)
              }
              error={error}
              onDismissError={() => setError(null)}
              onCancel={() => goToList()}
              onSave={(draft) => saveDraft(editorState, draft)}
            />
          ) : (
            <EditorNotFound onBack={() => goToList()} />
          )}
        </section>
      ) : (
        <section className="brain-content">
          <header className="brain-content-header">
            <div>
              <h1>{scopeLabel}</h1>
              <p>{visibleSkills.length} skills</p>
            </div>
            {canManageHere && (
              <button type="button" className="brain-primary-button" onClick={openCreate}>
                <Plus size={14} strokeWidth={1.7} />
                <span>New skill</span>
              </button>
            )}
          </header>

          <div className="brain-field-row">
            <label className="brain-field">
              <span>Search</span>
              <input value={query} onChange={(e) => setQuery(e.target.value)} />
            </label>
            <label className="brain-field">
              <span>Status</span>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <label className="brain-field">
              <span>Agent</span>
              <select value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)}>
                {AGENT_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {error && (
            <div className="brain-error">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)}>
                dismiss
              </button>
            </div>
          )}

          {loading ? (
            <div className="brain-loading">Loading...</div>
          ) : visibleSkills.length === 0 ? (
            <div className="brain-empty">
              <p>No skills in {scopeLabel}.</p>
              {canManageHere && (
                <button type="button" className="brain-primary-button" onClick={openCreate}>
                  <Plus size={14} strokeWidth={1.7} />
                  <span>Create skill</span>
                </button>
              )}
            </div>
          ) : (
            <ul className="brain-flat-list">
              {visibleSkills.map((skill) => (
                <li key={skill._id} className="brain-row brain-row-card">
                  <div className="brain-row-main">
                    <div className="brain-row-title">
                      <span>{skill.displayName}</span>
                      {!skill.isActive && <span className="brain-row-tag muted">Inactive</span>}
                      <span className="brain-row-tag">v{skill.version}</span>
                      <span className="brain-row-tag">{skill.contentHash.slice(0, 8)}</span>
                    </div>
                    <p className="brain-row-description">
                      <span className="brain-row-description-label">{skill.name}</span>
                      {skill.description ? ` - ${skill.description}` : ''}
                    </p>
                    <p className="brain-row-description">
                      <span className="brain-row-description-label">Use </span>
                      {skill.whenToUse}
                    </p>
                    <p className="brain-row-description">
                      {skill.agentTypes.length === 0 ? 'All agents' : skill.agentTypes.join(', ')}
                    </p>
                  </div>
                  {canManageHere && (
                    <div className="brain-row-actions">
                      <button type="button" onClick={() => toggleSkill(skill)}>
                        <Power size={13} strokeWidth={1.7} />
                        <span>{skill.isActive ? 'Disable' : 'Enable'}</span>
                      </button>
                      <button type="button" onClick={() => openDuplicate(skill)}>
                        <Copy size={13} strokeWidth={1.7} />
                        <span>Duplicate</span>
                      </button>
                      <button type="button" onClick={() => openEdit(skill)}>
                        Edit
                      </button>
                      <button type="button" className="danger" onClick={() => deleteSkill(skill)}>
                        <Trash2 size={13} strokeWidth={1.7} />
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function EditorNotFound({ onBack }: { onBack: () => void }) {
  return (
    <div className="brain-empty">
      <p>Skill not found.</p>
      <button type="button" className="brain-secondary-button" onClick={onBack}>
        <ArrowLeft size={14} strokeWidth={1.7} />
        <span>Back to skills</span>
      </button>
    </div>
  );
}

function SkillEditorPage({
  state,
  workspaces,
  canManageOrg,
  canManageWorkspace,
  canSave,
  error,
  onDismissError,
  onCancel,
  onSave,
}: {
  state: EditorState;
  workspaces: ScopeWorkspace[];
  canManageOrg: boolean;
  canManageWorkspace: boolean;
  canSave: boolean;
  error: string | null;
  onDismissError: () => void;
  onCancel: () => void;
  onSave: (draft: Draft) => Promise<void>;
}) {
  const [local, setLocal] = useState<Draft>(state.draft);
  const [saving, setSaving] = useState(false);
  const isEditing = state.kind === 'edit';

  const setField = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setLocal((prev) => {
      const next = { ...prev, [key]: value };
      if (!isEditing && key === 'displayName' && !prev.name) {
        next.name = slugify(String(value));
      }
      return next;
    });
  };

  const setScope = (scope: SkillScope) => {
    setLocal((prev) => ({
      ...prev,
      scope,
      workspaceId: scope === 'workspace' ? prev.workspaceId || workspaces[0]?._id : undefined,
    }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSave) return;
    if (
      !local.name.trim() ||
      !local.displayName.trim() ||
      !local.description.trim() ||
      !local.whenToUse.trim() ||
      !local.contentMarkdown.trim()
    ) {
      return;
    }

    setSaving(true);
    await onSave({
      ...local,
      name: local.name.trim(),
      displayName: local.displayName.trim(),
      description: local.description.trim(),
      whenToUse: local.whenToUse.trim(),
      contentMarkdown: local.contentMarkdown.trim(),
      agentTypes: local.agentTypes.filter(Boolean),
    });
    setSaving(false);
  };

  const availableScopes: Array<{ value: SkillScope; label: string; disabled?: boolean }> = [
    { value: 'personal', label: 'Personal' },
    { value: 'workspace', label: 'Workspace', disabled: !canManageWorkspace || workspaces.length === 0 },
    { value: 'organization', label: 'Organization', disabled: !canManageOrg },
  ];

  const title = isEditing ? local.displayName || 'Edit skill' : state.kind === 'duplicate' ? 'Duplicate skill' : 'New skill';
  const subtitle =
    state.kind === 'duplicate'
      ? `Copying ${state.source.displayName}`
      : isEditing
        ? `${local.name} - v${state.existing.version} - ${state.existing.contentHash.slice(0, 8)}`
        : 'Create a reusable agent procedure.';

  return (
    <form onSubmit={submit} className="brain-skill-editor-page">
      <header className="brain-skill-editor-header">
        <button type="button" className="brain-secondary-button" onClick={onCancel}>
          <ArrowLeft size={14} strokeWidth={1.7} />
          <span>Back</span>
        </button>
        <div>
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
        <button type="submit" className="brain-primary-button" disabled={saving || !canSave}>
          <Save size={14} strokeWidth={1.7} />
          <span>{saving ? 'Saving...' : isEditing ? 'Save changes' : 'Create skill'}</span>
        </button>
      </header>

      {error && (
        <div className="brain-error">
          <span>{error}</span>
          <button type="button" onClick={onDismissError}>
            dismiss
          </button>
        </div>
      )}

      {!canSave && (
        <div className="brain-error">
          <span>You do not have permission to save skills in this scope.</span>
        </div>
      )}

      <div className="brain-skill-editor-grid">
        <section className="brain-skill-editor-main">
          <label className="brain-field brain-skill-markdown-field">
            <span>Instructions</span>
            <textarea
              value={local.contentMarkdown}
              onChange={(e) => setField('contentMarkdown', e.target.value)}
              className="brain-skill-markdown-editor"
              required
              autoFocus
            />
            <small>{local.contentMarkdown.length}/50000</small>
          </label>
        </section>

        <aside className="brain-skill-editor-sidebar">
          {!isEditing && (
            <div className="brain-field-row single">
              <label className="brain-field">
                <span>Scope</span>
                <select value={local.scope} onChange={(e) => setScope(e.target.value as SkillScope)}>
                  {availableScopes.map((scope) => (
                    <option key={scope.value} value={scope.value} disabled={scope.disabled}>
                      {scope.label}
                    </option>
                  ))}
                </select>
              </label>
              {local.scope === 'workspace' && (
                <label className="brain-field">
                  <span>Workspace</span>
                  <select value={local.workspaceId || ''} onChange={(e) => setField('workspaceId', e.target.value)}>
                    {workspaces.map((workspace) => (
                      <option key={workspace._id} value={workspace._id}>
                        {workspace.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}

          <label className="brain-field">
            <span>Display name</span>
            <input value={local.displayName} onChange={(e) => setField('displayName', e.target.value)} required />
          </label>

          <label className="brain-field">
            <span>Slug</span>
            <input
              value={local.name}
              onChange={(e) => setField('name', sanitizeSlugInput(e.target.value))}
              required
              pattern="[a-z][a-z0-9_-]{1,63}"
              title="Start with a lowercase letter. Use lowercase letters, numbers, hyphens, and underscores."
              disabled={isEditing}
            />
            <small>
              {isEditing
                ? 'Immutable after create. Agents call Skill with this exact name.'
                : 'Exact Skill tool name. Start with a letter; use lowercase letters, numbers, hyphens, and underscores.'}
            </small>
          </label>

          <label className="brain-field">
            <span>Description</span>
            <textarea value={local.description} onChange={(e) => setField('description', e.target.value)} rows={4} required />
            <small>{local.description.length}/1000</small>
          </label>

          <label className="brain-field">
            <span>When to use</span>
            <textarea value={local.whenToUse} onChange={(e) => setField('whenToUse', e.target.value)} rows={5} required />
            <small>{local.whenToUse.length}/2000</small>
          </label>

          <label className="brain-field">
            <span>Agent target</span>
            <select
              value={local.agentTypes[0] || ''}
              onChange={(e) => setField('agentTypes', e.target.value ? [e.target.value] : [])}
            >
              {AGENT_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="brain-field-inline">
            <input type="checkbox" checked={local.isActive} onChange={(e) => setField('isActive', e.target.checked)} />
            <span>Active</span>
          </label>
        </aside>
      </div>
    </form>
  );
}
